import React, { Component, Context, CSSProperties } from "react";
import clsx from "clsx";
import LayoutContext, { ILayoutContext } from "../../../contexts/layout/LayoutContext";
import { IImage, VideoMedia } from "../../../models/Image";
import enlargeIcon from "../../../assets/icons/icon-enlarge.svg";
import playIcon from "../../../assets/icons/play-icon.svg";
// import LayoutUtils from "../../../utils/layout.ts__";
import VideoPlayer from "./VideoPlayer";
import { sleep } from "../../../utils";
import UILoader from "../UILoader";
import { FileType } from "../../../laco-common/base/models/IFile";
import "./ImageView.scss";

export type ImageProps = {
	// image: IImage,
	viewWidth?: number;
	viewHeight?: number;
	imageWidth100Pct?: boolean;
	imageHeight100Pct?: boolean;
	isPercentage?: boolean;
	renderVideo?: boolean;
	autoWidth?: boolean;
	containImage?: boolean;
	viewFull?: boolean;
	displayThumb?: boolean; // display thumbnail prop
	awaitImgExists?: boolean; // for freshly created thumbs
	displayEnlargeHover?: boolean;
	enlargeText?: string;
	displayVidHover?: boolean;
	className?: string;
	// styledImage?: string;
	index?: number;
	// reloadImage?: boolean;
	onClick?: (image?: any, i?: number) => void;
	enlargeCallback?: (image: any, i?: number) => void;
	onloadCallback?: (image: any, divRef: any, height: number) => void;
	scaleInEffect?: boolean;
	renderContainer?: boolean;
};

type ImageViewComponentProps = ImageProps & { image: IImage; showEmptyImgWhenEmpty?: boolean };

// component handles image alignment, centering and caching
// also supports hover effect
class ImageView extends Component<ImageViewComponentProps> {
	static contextType: Context<ILayoutContext> = LayoutContext;
	private _isMounted: boolean = false;
	private img: HTMLImageElement = new Image();
	private divRef: any;
	private setRef: any;
	private imageHeight: number;
	// private width: number = this.props.viewWidth || this.context.imageWidth;
	// private height: number = this.props.viewHeight || this.context.imageHeight;

	state = {
		loading: true,
		image: this.props.image, // update when loaded to adjust view
	};

	constructor(props: any) {
		super(props);
		this.divRef = React.createRef();
	}

	getWidth(): string {
		if (this.props.imageWidth100Pct || this.context.imageWidth100Pct || this.props.isPercentage) return "100%";
		if (this.props.viewWidth || this.context.imageWidth) {
			return `${this.props.viewWidth || this.context.imageWidth}${this.getUnits()}`;
		}
		return "100%";
	}

	// getHeight(): string {
	// 	if (this.props.viewHeight || this.context.imageHeight) {
	// 		return `${this.props.viewHeight || this.context.imageHeight}${this.getUnits()}`;
	// 	}
	// 	return "auto";
	// }

	getHeight(elHeight?: boolean): string {
		if (this.props.imageHeight100Pct || this.context.imageHeight100Pct || this.props.isPercentage) return "100%";
		if (this.props.viewHeight) {
			if (isNaN(this.props.viewHeight as any)) return this.props.viewHeight as any as string;
			return `${this.props.viewHeight}${this.getUnits()}`;
		}
		if (this.props.isPercentage) return "auto";
		if (elHeight && !this.context.imageHeight) {
			return "94vw";
		}
		return `${this.context.imageHeight}${this.getUnits()}`;
		// if (this.props.viewHeight) {
		// 	return `${this.props.viewHeight}${this.getUnits()}`;
		// }
		// return "auto";
	}

	getUnits(): string {
		if (this.props.isPercentage) {
			return "%";
		}
		return "px";
	}

	getUrl(image: IImage): string {
		if (!image) return "";
		if (this.props.displayThumb) {
			return image.thumbUrl || image.url;
		}
		return image.url;
	}

	getBackgroundSize(): string {
		if (this.props.containImage) {
			return "contain";
		}
		if (this.props.autoWidth) {
			return `${this.getWidth()} auto`;
		}

		// center image to avoid distortion
		if ((this.img.width || 0) < (this.img.height || 0)) {
			return `${this.getWidth()} auto`;
		} else if ((this.img.width || 0) > (this.img.height || 0)) {
			// return `auto ${this.getHeight()}`; // dana
			return "cover";
		}

		return `${this.getWidth()} ${this.getHeight()}`; // will use same dimensions, good for equal width and height
	}

	componentDidMount() {
		this._isMounted = true;
		if (!this.img) {
			this.img = new Image();
		}
		this.init(this.props);
	}

	componentWillUnmount() {
		this._isMounted = false;
		if (this.img) {
			this.img.onload = null;
			this.img = new Image();
			// delete this.img;
		}
	}

	componentWillReceiveProps(nextProps: ImageViewComponentProps) {
		this.init(nextProps);
	}

	async init(props: ImageViewComponentProps) {
		// set image in state
		const image: IImage = props.image;

		// handle image load
		const urlToLoad: string = this.getUrl(image);
		// check if need to reload img
		if (this.img && this.img.src && this.img.src === urlToLoad && this.img.onload) {
			this.setState({ loading: false });
			return; // same image, no need to reload
		}

		// cleanup if needed
		if (this.img.onload) {
			this.img.onload = null;
		}
		this.img.src = "";

		// init with url
		// cache image for faster load
		this.img.src = urlToLoad;
		if (props.awaitImgExists) {
			this.setState({ loading: true });
			const urlExists: boolean = await this.isUrlFound(urlToLoad);
			if (urlExists && this._isMounted) {
				this.setState({ loading: false });
			} else {
				// wait for thumb image generation
				this.setState({ loading: true });
				const loaded = await this.pollForExistingImage(urlToLoad, image);
				this.setState({ loading: false, image: this.forceRefreshImage(image) });
				this.forceUpdate();
			}
		} else {
			const urlExists: boolean = await this.isUrlFound(urlToLoad);
			if (!urlExists) {
				this.setState({ loading: false });
			} else {
				this.setState({ loading: false });
			}
		}

		if (props.autoWidth || (props.renderVideo && !props.displayThumb)) {
			this.setState({ image, loading: false });
			return;
		}

		if (this.img.complete) {
			// image has already loaded - browser cache
			this.imgOnLoad(props);
		} else {
			// register onload evt
			this.img.onload = (e: any) => {
				this.imgOnLoad(props);
			};
		}
	}

	imgOnLoad(props: ImageViewComponentProps) {
		this.setState({ image: props.image, loading: false }); // new image or first
		if (props.onloadCallback) {
			const ratio: number = (this.props.viewWidth || this.context.imageWidth) / this.img.width;
			const height: number = this.img.height * ratio;
			props.onloadCallback(this.img, this.divRef, this.imageHeight || height);
			setTimeout(() => {
				props.onloadCallback(this.img, this.divRef, this.imageHeight || height);
			}, 1000);
		}
	}

	forceRefreshImage(image: IImage): IImage {
		return {
			...image,
			url: image.url ? (image.url += "&refresh=" + Date.now()) : "",
			thumbUrl: image.thumbUrl ? (image.thumbUrl += "&refresh=" + Date.now()) : "",
		};
	}

	async pollForExistingImage(urlToLoad: string, image: IImage, pollCount: number = 0): Promise<boolean> {
		if (pollCount >= 3) {
			return false;
		}
		await sleep(async () => {
			const urlExists: boolean = await this.isUrlFound(urlToLoad);
			if (this._isMounted) {
				if (urlExists) {
					this.setState({ loading: false, image });
					return true;
				} else {
					return await this.pollForExistingImage(urlToLoad, image, pollCount + 1);
				}
			}
		}, 4000);
	}

	async isUrlFound(url): Promise<boolean> {
		if (!url) return true;
		try {
			const response = await fetch(url, {
				method: "GET",
				// cache: "no-cache",
			});
			return response.status === 200;
		} catch (error) {
			return false;
		}
	}

	renderImageEnlargeHover() {
		if (!this.props.displayEnlargeHover && !this.props.enlargeCallback) return null;

		if (!this.props.displayEnlargeHover) {
			return (
				<div
					className="image-hover-enlarge d-flex flex-column align-content-center justify-content-center align-self-center"
					onClick={() => this.props.enlargeCallback(this.state.image)}
				></div>
			);
		}

		if (this.state.image.fileType == FileType.VIDEO) {
			return (
				<div
					className="image-hover-enlarge video-thumbnail pt-3 d-flex flex-column align-content-center justify-content-center align-self-center"
					onClick={() => this.props.enlargeCallback(this.state.image)}
				>
					<img className="play-vid-icon" src={playIcon}></img>
				</div>
			);
		} else {
			return (
				<div
					className="image-hover-enlarge d-flex flex-column align-content-center justify-content-center align-self-center"
					onClick={this.props.enlargeCallback}
				>
					<img className="rectangle-enlarge" src={enlargeIcon}></img>
					<span
						className="enlarge-text"
						// style={{ left: this.getLeft() }}
					>
						{this.props.enlargeText || "Click to Enlarge"}
					</span>
				</div>
			);
		}
	}

	// getLeft(): number {
	//     if (this.props.enlargeText) {
	//         return Math.round((this.props.viewWidth - (LayoutUtils.isDesktopRes() ? 110 : 80)) / 2);
	//     }
	//     return Math.round((this.props.viewWidth - (LayoutUtils.isDesktopRes() ? 140 : 100)) / 2);
	// }

	getLeft(): string {
		// if (this.props.enlargeText) {
		// 	return Math.round((this.props.viewWidth - (LayoutUtils.isDesktopRes() ? 110 : 80)) / 2);
		// }
		// return Math.round((this.props.viewWidth - (LayoutUtils.isDesktopRes() ? 140 : 100)) / 2);
		return "32%"; // 110
	}

	// currrently not in use
	renderVideo() {
		return (
			<>
				<VideoPlayer
					width={"100%"}
					video={this.state.image as VideoMedia}
					videoId={this.state.image.docId + this.state.image.filename}
					autoPlay={false}
				></VideoPlayer>
				{this.props.children}
			</>
		);
	}

	// getRatio(): number {
	// 	if (isNaN(this.props.viewWidth as any)) return 1;
	// 	return (this.props.viewWidth as any) / this.img.width;
	// }

	render() {
		if (!this.state.image) return null;
		if (this.props.renderVideo) {
			return this.renderVideo();
		}

		const url: string = this.getUrl(this.state.image);
		const { scaleInEffect, renderContainer } = this.props;
		let containerStyles: CSSProperties = {
			width: this.getWidth(),
			height: this.getHeight(),
			maxWidth: "100%",
		};
		const imageStyles: CSSProperties = {
			width: "100%",
			height: "100%",
			backgroundPosition: "center",
			backgroundSize: this.getBackgroundSize(),
		};
		if (url) {
			imageStyles.backgroundImage = "url('" + this.getUrl(this.state.image) + "')";
		}

		if (this.props.viewFull) {
			// handle 100% here
			// if you want min viewHeight do it here
			// otherwise the below works just fine

			// determine if vertical or horizontal image
			const isHorizontal: boolean = this.img.width >= this.img.height;
			const ratio: number = (this.props.viewWidth || this.context.imageWidth) / this.img.width;
			// imgStyle.width = width;
			this.imageHeight = this.img.height * ratio;
			containerStyles.height = this.imageHeight + "px";
			//imgStyle.backgroundSize = isHorizontal ? "auto 100%" : "100% auto";
			imageStyles.backgroundSize = "auto 100%";
		}
		if (!scaleInEffect && !renderContainer) {
			containerStyles = { ...imageStyles, ...containerStyles };
		}

		return (
			<div
				className={clsx(
					"mss-image-view",
					this.props.className,
					this.state.loading && "image-loading ",
					!this.props.image.url && this.props.showEmptyImgWhenEmpty && "show-empty-image"
				)}
				data-image-index={this.props.index}
				ref={(el) => (this.divRef = el)}
				style={containerStyles}
				title={this.state.image.title}
				onClick={this.props.onClick}
			>
				{(scaleInEffect || renderContainer) && (
					<div
						className={clsx("mss-image-view-image", scaleInEffect && "scale-on-hover")}
						style={imageStyles}
					/>
				)}
				<UILoader display={this.state.loading} loaderStyle={{ margin: "30% auto" }} />
				{!this.state.loading && this.renderImageEnlargeHover()}
				{this.props.children}
			</div>
		);
	}
}

export default ImageView;
